/** Canlı destek widget'ının yüklenme koşulu ve oturum bilgisi.
 *
 * Widget'ı yalnızca ``LiveChatSupport`` çizer; sayfa içindeki kısayollar
 * (ör. canlı destekten talep edilen bonus kartı) durumu ``liveChatState`` ile
 * sorup pencereyi ``openLiveChat`` ile açar — böylece "widget var mı" kuralı
 * tek yerde kalır.
 */

const licenseOf = (settings = {}) => {
  const license = String(settings.livechat_license_id || '').trim();
  if (!settings.livechat_enabled) return '';
  return /^\d{8,20}$/.test(license) ? license : '';
};

/* ---------- Comm100 ----------
 *
 * Canlı Betrun desteği Comm100'dür (22.10.2026). Ayar `/settings` →
 * `comm100` (sunucu: services/comm100.py); yoksa `null`. Betik `Comm100Support`
 * ile yüklenir; özel koddan zaten yüklendiyse (`window.Comm100API.site_id`)
 * ikinci kez yüklenmez. LiveChat kuruluysa önce o açılır. */
const COMM100_PLAN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Comm100 kullanılabilir mi: ayar tam ve (gerekiyorsa) üye girişi var. */
export const comm100Config = (settings = {}, user = null) => {
  const config = settings?.comm100;
  if (!config || typeof config !== 'object') return null;
  const hosts = Array.isArray(config.hosts) ? config.hosts.filter(Boolean) : [];
  if (!/^\d{3,12}$/.test(String(config.site_id || '')) || !COMM100_PLAN.test(String(config.code_plan || ''))
      || !hosts.length) return null;
  if (config.require_login && !user) return null;
  return { ...config, hosts };
};

/** Comm100 penceresini açar; betik yüklü değilse false. */
export const openComm100 = (plan = '') => {
  const api = typeof window !== 'undefined' ? window.Comm100API : null;
  if (typeof api?.do !== 'function') return false;
  const target = plan || api.main_code_plan
    || document.querySelector('[id^="comm100-button-"]')?.id.slice('comm100-button-'.length) || '';
  if (!target) return false;
  try {
    api.do('livechat.button.click', target);
    return true;
  } catch {
    return false;
  }
};

/** Kabukların "Canlı Destek" düğmesi: LiveChat ya da Comm100 kurulu mu. */
export const liveSupportEnabled = (settings = {}, user = null) => (
  Boolean(licenseOf(settings) && liveChatState(settings, user) === 'ready')
  || Boolean(comm100Config(settings, user))
  // Geçişte taşınan özel koddaki Comm100 (panelde henüz ayara alınmamış).
  || ['header', 'body', 'footer'].some((slot) => /Comm100API/.test(String(settings?.custom_code?.[slot] || '')))
);

/** 'off' = widget hiç yüklenmez, 'login_required' = üye girişi bekleniyor. */
export const liveChatState = (settings = {}, user = null) => {
  if (!licenseOf(settings)) {
    // LiveChat yoksa Comm100'e bakılır (aynı sözleşme: kart ve kısayollar).
    if (!settings?.comm100) return 'off';
    return comm100Config(settings, user) ? 'ready' : (settings.comm100.require_login && !user ? 'login_required' : 'off');
  }
  if (settings.livechat_require_login && !user) return 'login_required';
  return 'ready';
};

export const buildLiveChatConfig = (settings = {}, user = null) => {
  const license = licenseOf(settings);
  if (liveChatState(settings, user) !== 'ready') return null;

  const group = String(settings.livechat_group_id || '').trim();
  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    : '';
  const customerName = fullName || user?.username || undefined;

  return {
    license,
    group: group || undefined,
    customerName,
    customerEmail: user?.email || undefined,
    sessionVariables: user ? {
      user_id: String(user.id || ''),
      username: String(user.username || ''),
    } : {
      visitor_type: 'guest',
    },
  };
};

/** Canlı destek penceresini açar; widget yüklü değilse false döner. */
export const openLiveChat = () => {
  const widget = typeof window !== 'undefined' ? window.LiveChatWidget : null;
  // LiveChat yoksa Comm100 (aynı düğmeler, bkz. comm100Config).
  if (typeof widget?.call !== 'function') return openComm100();
  // `maximize` gizlenmiş widget'ta çalışmaz (LiveChat sözleşmesi). Bastırma
  // etkinken — hazır spor kitabı açıkken — kabuklardaki "Canlı Destek"
  // düğmeleri bu yüzden sessizce hiçbir şey yapmıyordu: pencere önce görünür
  // kılınır, sonra büyütülür.
  let visibility = '';
  try {
    visibility = widget.get?.('state')?.visibility || '';
  } catch {
    visibility = '';
  }
  if (visibility === 'hidden') widget.call('minimize');
  widget.call('maximize');
  return true;
};

/* ---------- Bastırma ----------
 *
 * Tam ekran sağlayıcı çerçeveleri (oyun modalı, hazır spor kitabı) ekranın sağ
 * alt köşesini kendi düğmeleri için kullanır: Betby kuponu ve "Bahis yap"
 * düğmesi tam oraya düşer, 92px'lik canlı destek düğmesi ise onların üstünü
 * örtüyor ve oyuncu kuponu göremiyordu.
 *
 * Sayaçlıdır: iç içe açılan iki çerçeveden biri kapandığında widget geri
 * gelmemelidir. Durum modül belleğindedir — widget'ı yalnızca
 * ``LiveChatSupport`` çizer ve bastırmayı isteyen taraf onu bulamaz. */
let suppressCount = 0;
const suppressListeners = new Set();

const notifySuppression = () => {
  const suppressed = suppressCount > 0;
  suppressListeners.forEach((listener) => {
    try {
      listener(suppressed);
    } catch {
      // Bir dinleyicinin hatası diğerlerini durdurmamalı.
    }
  });
};

export const isLiveChatSuppressed = () => suppressCount > 0;

/** Widget'ı gizler; dönen fonksiyon (efekt temizliği) onu geri açar. */
export const suppressLiveChat = () => {
  suppressCount += 1;
  if (suppressCount === 1) notifySuppression();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    suppressCount = Math.max(0, suppressCount - 1);
    if (suppressCount === 0) notifySuppression();
  };
};

/** Bastırma değişimini dinler; dönen fonksiyon aboneliği bırakır. */
export const subscribeLiveChatSuppression = (listener) => {
  suppressListeners.add(listener);
  return () => { suppressListeners.delete(listener); };
};
